import { handleFreeTrialStart } from '@src/state/payment/actions'
import { hardRedirect } from '@src/state/workspace/actions'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import * as Styled from './styled'

/**
 * Used to signal the feature text should be
 * rendered as a checkmark
 */
const CHECK = 'CHECK'
export const UPGRADE_TO_EASYBIB_PLUS = 'UPGRADE_TO_EASYBIB_PLUS'
export const UPGRADE_TO_EASBIB_STUDY_PLUS = 'UPGRADE_TO_EASBIB_STUDY_PLUS'
export const UPGRADE_TO_EASBIB_STUDY_PLUS_URL =
  'https://www.chegg.com/study-pack/?intcid=wt.Easy-Bib.pricetabletest5112020'
export const EASYBIB_FREE = 'easybib-free'
export const EASYBIB_PLUS = 'easybib-plus'
export const EASYBIB_PLUS_STUDY_PACK = 'easybib-plus-study-pack'

/**
 * Features consist of an id that is used by products to describe
 * feature participation, and a name that will be used to generate
 * the row headers for each feature
 */
interface Feature {
  id: string
  name: string
}

/**
 * One of the products to be rendered in the column,
 * with a list of descriptions of feature participation
 * for each product
 */
interface Product {
  id: string
  name: string
  features: {
    [key: string]: string
  }
  price: string
  recommended?: boolean
}

interface PricingTableProps {
  title: string
  features: Feature[]
  products: Product[]
  /**
   * URL to terms and conditions
   */
  termsAndConditionsUrl: string
  currentProductId?: string
  onSignUpClick: (event: React.MouseEvent, type?: string) => void
}

const tableData: {
  title: string
  features: Feature[]
  products: Product[]
  termsAndConditionsUrl: string
} = {
  title: 'Choose your online writing help',
  features: [
    { id: 'citationStyle', name: 'Citation styles' },
    { id: 'grammarChecks', name: 'Grammar checks' },
    { id: 'saveCitations', name: 'Save your citations' },
    { id: 'plagiarismDetection', name: 'Plagiarism detection' },
    { id: 'expertPaperHelp', name: 'Expert help for your paper' },
    { id: 'textbookSolutions', name: 'Millions of Textbook Solutions & Expert Q&A' },
    { id: 'practiceProblems', name: 'Practice problems & video solutions**' },
    { id: 'mathSolver', name: 'Instant math solver' },
  ],
  products: [
    {
      id: EASYBIB_FREE,
      name: 'EasyBib® free account',
      price: 'Free',
      features: {
        citationStyle: 'MLA only',
        grammarChecks: 'Only first 5 errors checked',
        saveCitations: CHECK,
      },
    },
    {
      id: EASYBIB_PLUS,
      name: 'EasyBib® Plus 3-day free trial*',
      price: '$9.95/mo.',
      recommended: true,
      features: {
        citationStyle: '7000+ styles including APA & Chicago',
        grammarChecks: 'Unlimited',
        saveCitations: CHECK,
        plagiarismDetection: CHECK,
        expertPaperHelp: '15 papers a month',
      },
    },
    {
      id: EASYBIB_PLUS_STUDY_PACK,
      name: 'EasyBib® Plus & Chegg® Study Pack',
      price: '$19.95/mo.',
      features: {
        citationStyle: '7000+ styles including APA & Chicago',
        grammarChecks: 'Unlimited',
        saveCitations: CHECK,
        plagiarismDetection: CHECK,
        expertPaperHelp: 'Unlimited',
        textbookSolutions: CHECK,
        practiceProblems: CHECK,
        mathSolver: CHECK,
      },
    },
  ],
  termsAndConditionsUrl:
    'https://www.easybib.com/guides/free-trial-terms-and-conditions/',
}

const ProductFeature: React.FC<{ product: Product; feature: string }> = ({
  product,
  feature,
}) => {
  const featureName = product.features[feature] || ''
  const isCheck = featureName === CHECK

  return (
    <Styled.Feature>
      {isCheck ? <Styled.Check size="large" /> : featureName}
    </Styled.Feature>
  )
}

export const PricingTable: React.FC<PricingTableProps> = ({
  title,
  features,
  products,
  termsAndConditionsUrl,
  currentProductId,
  onSignUpClick,
}) => {
  return (
    <Styled.Container>
      <Styled.Table
        role="table"
        aria-label="Membership"
        aria-describedby="membership_table_desc"
      >
        <Styled.Title id="membership_table_desc">{title}</Styled.Title>
        <Styled.Products role="rowgroup">
          <Styled.ProductNames role="row">
            <Styled.Spacer />
            {products.map(p => (
              <Styled.ProductName role="columnheader" key={p.id}>
                {p.name}
              </Styled.ProductName>
            ))}
          </Styled.ProductNames>
        </Styled.Products>
        <Styled.Features role="rowgroup">
          {features.map(feature => (
            <Styled.FeatureRow role="row" key={feature.name}>
              <Styled.FeatureWrapper role="rowheader">
                <Styled.Feature>{feature.name}</Styled.Feature>
              </Styled.FeatureWrapper>
              {products.map(product => (
                <Styled.FeatureWrapper
                  role="cell"
                  recommended={product.recommended}
                  key={product.id}
                >
                  <ProductFeature product={product} feature={feature.id} />
                </Styled.FeatureWrapper>
              ))}
            </Styled.FeatureRow>
          ))}
        </Styled.Features>
        <Styled.PricesAndActions role="rowgroup">
          <Styled.Prices role="row">
            <Styled.Spacer />
            {products.map(product => (
              <Styled.Price
                role="cell"
                recommended={product.recommended}
                key={product.id}
              >
                <div>{product.price}</div>
              </Styled.Price>
            ))}
          </Styled.Prices>
          <Styled.Actions>
            <Styled.Spacer />
            {products.map(product => (
              <Styled.Action
                role="cell"
                key={product.id}
                recommended={product.recommended}
              >
                <Styled.Link
                  href=""
                  recommended={product.recommended}
                  data-test={product.id}
                  current={currentProductId === product.id}
                  onClick={e => onSignUpClick(e, product.id)}
                >
                  {currentProductId === product.id ? 'Current' : 'Sign up'}
                </Styled.Link>
              </Styled.Action>
            ))}
          </Styled.Actions>
        </Styled.PricesAndActions>
        <Styled.Footer>
          <Styled.TermsAndConditions href={termsAndConditionsUrl} target="_blank">
            *See Terms and Conditions
          </Styled.TermsAndConditions>
        </Styled.Footer>
      </Styled.Table>
    </Styled.Container>
  )
}

const mapStateToProps = () => ({
  title: tableData.title,
  features: tableData.features,
  products: tableData.products,
  termsAndConditionsUrl: tableData.termsAndConditionsUrl,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSignUpClick: (event: React.MouseEvent, productId: string) => {
    event.preventDefault()
    if (productId === EASYBIB_PLUS) {
      dispatch(handleFreeTrialStart(UPGRADE_TO_EASYBIB_PLUS))
    } else if (productId === EASYBIB_PLUS_STUDY_PACK) {
      dispatch(
        hardRedirect(
          { url: UPGRADE_TO_EASBIB_STUDY_PLUS_URL },
          { label: UPGRADE_TO_EASBIB_STUDY_PLUS }
        )
      )
    }
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(PricingTable)



// WEBPACK FOOTER //
// ./src/components/PricingTable/index.tsx